import React, { type FunctionComponent, useEffect, useState } from 'react'
import { css } from '@emotion/react'
import { SVGIcon } from 'ic-snacks'
import {
  borderRadius,
  colors,
  Checkbox,
  defaultLineHeight,
  fontSize,
  spacing,
} from '@retailer-platform/shared-components'
import { DateRangePicker, SingleDatePicker } from 'react-dates'
import moment, { type Moment } from 'moment'
import { dateFormat } from 'domains/merchandising/constants/heroBanner.constants'
import { MerchandisingFormInputWrapper } from 'domains/merchandising/components/form-input/MerchandisingFormInputWrapper'
import { MerchandisingFormField } from 'domains/merchandising/components/form-field/MerchandisingFormField'
import { MerchandisingFormRow } from 'domains/merchandising/components/form-row/MerchandisingFormRow'
import { MerchandisingFormSelect } from 'domains/merchandising/components/form-input/MerchandisingFormSelect'
import timezones from 'domains/merchandising/constants/timezones'
import { useDashMessages, useDashIntl } from 'intl/intl.hooks'
import { type ControllerProps } from 'utils/react-hook-form/reactHookForm.types'

// interface for select options
type SelectOptionTypeTimezones = {
  offset: number
  label: string
  isDisabled: boolean
  value: string
}

// function to check if Dst is in effect right now
const isDst = (date = new Date()): boolean => {
  const january = new Date(date.getFullYear(), 0, 1).getTimezoneOffset()
  const july = new Date(date.getFullYear(), 6, 1).getTimezoneOffset()
  return Math.max(january, july) !== date.getTimezoneOffset()
}

// hide the timezones that are not in effect right now
const getTimezoneOptions = (
  data = timezones,
  date: Date = new Date()
): SelectOptionTypeTimezones[] =>
  data
    .map(timezone => ({
      offset: timezone.offset,
      label: timezone.label,
      isDisabled: timezone.isdst != isDst(date) || !timezone.utc?.[0], // disable any value that has no values
      value: timezone.utc?.[0] || '',
    }))
    .filter(timezone => !timezone.isDisabled)
    .sort((a, b) => a.offset - b.offset)

type DateType = Moment | null

type StartEndDate = {
  startDate: DateType
  endDate: DateType
}

interface Props extends Omit<ControllerProps, 'name'> {}

const wrapperStyles = css({
  alignItems: 'center',
  display: 'flex',
  maxWidth: '315px',
  width: '100%',

  // Overwrite some of the Date picker styles to fit with IDS
  '.DateRangePickerInput, .SingleDatePicker .DateInput_input': {
    borderRadius: borderRadius.X8,
    color: colors.GRAYSCALE.X50,
    fontSize: '13px', // use the same font size for range/single picker
    lineHeight: '13px',
    height: '46px',
  },

  '.DateRangePicker .DateInput_input__small': {
    color: colors.GRAYSCALE.X70,
  },
})

const checkboxWrapper = css({
  alignItems: 'center',
  display: 'flex',
  fontSize: fontSize.X12,
  marginLeft: spacing.X8,
  marginTop: '20px', // Offset the label
})

const checkboxLabelStyles = css({
  cursor: 'pointer',
})

const footnoteStyles = css({
  color: colors.GRAYSCALE.X70,
  display: 'flex',
  fontSize: fontSize.X12,
  lineHeight: defaultLineHeight,
  maxWidth: '645px',
  marginTop: spacing.X16,
  width: '100%',

  '& > svg': {
    marginRight: spacing.X4,
  },
})

export const BannerFormDetailScheduler: FunctionComponent<Props> = ({ form }) => {
  const { register, getValues, setValue } = form
  const originalStartDate = getValues('scheduling.startDate')
  const originalEndDate = getValues('scheduling.endDate')

  // Set dates if a response is returned from API, if not start date is today, and no end date is set
  // State is used for visual representation in the input field only.
  const [dates, setDates] = useState({
    startDate: originalStartDate ? moment(originalStartDate) : moment(),
    endDate: originalEndDate ? moment(originalEndDate) : null,
  })

  // Focused state for date range
  const [focusedInput, setFocusedInput] = useState(null)

  // Handle date picker range change (start/end date)
  const onDateRangeChange = (newDates: StartEndDate) => {
    // Set state for visual form field
    setDates(newDates)

    // Set value for react hook form
    setValue('scheduling.startDate', newDates.startDate?.utc())
    setValue('scheduling.endDate', newDates.endDate?.utc())
  }

  const messages = useDashMessages({
    datesLabel: 'merchandising.hero-banners.section.detail.field.dates.label',
    checkboxDescription: 'merchandising.hero-banners.section.detail.field.dates.checkbox',
    defaultEndDate: 'merchandising.hero-banners.field.empty.value',
  })

  const intl = useDashIntl()

  return (
    <>
      <MerchandisingFormRow>
        <div css={wrapperStyles}>
          <MerchandisingFormInputWrapper label={messages.datesLabel}>
            <DateRangePicker
              small
              withPortal
              minimumNights={0}
              startDateId="startDate"
              endDateId="endDate"
              startDate={dates.startDate}
              endDate={dates.endDate}
              focusedInput={focusedInput}
              onFocusChange={focusedInput => setFocusedInput(focusedInput)}
              onDatesChange={newDates => onDateRangeChange(newDates)}
            />
          </MerchandisingFormInputWrapper>
        </div>

        <MerchandisingFormField>
          <MerchandisingFormSelect
            form={form}
            identifier="scheduling.timezone"
            options={getTimezoneOptions()}
            labelId={'merchandising.hero-banners.section.detail.field.timezone.label'}
          />
        </MerchandisingFormField>
      </MerchandisingFormRow>
      <input name="scheduling.startDate" ref={register} type="hidden" />
      <input name="scheduling.endDate" ref={register} type="hidden" />
    </>
  )
}
